import {
  CloseButton,
  CrudMode,
  DropDownMenu,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  deepCopy,
  newToast,
} from "@compass/core";
import { Select, SubmitButton } from "components";
import {
  LkStudentDisplayFieldId,
  Permission,
  Student,
  StudentApplicationFilterResource,
  StudentApplicationListResource,
  StudentApplicationResponseDto,
  StudentEnrollment,
} from "generated/openApi";
import { ApplicationStatus } from "pages/cobro/applications/ApplicationPropertyEnum";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";

type StudentApplicationHeaderProps = {
  rootPermission: Permission;
  crudMode: CrudMode;
  applicationDto: StudentApplicationResponseDto | undefined;
  updateApplicationDto: (dto: StudentApplicationResponseDto) => void;
  printApplication: () => void;
  isApproving: boolean;
  canReview: boolean;
  isReviewing: boolean;
  hasChanges: boolean;
  applicationResource: StudentApplicationListResource;
  updateIsApproving: (value: boolean) => void;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  newStudent: Student;
  reviewStudentEnrollment: StudentEnrollment | undefined;
};

const scrollToReviewCard = () => {
  const element = document.getElementById("review-card");
  if (element) {
    element.scrollIntoView(true);
  }
};

export const StudentApplicationHeader = ({
  applicationDto,
  isApproving,
  canReview,
  isReviewing,
  hasChanges,
  applicationResource,
  updateIsApproving,
  updateApplicationDto,
  setValidationErrors,
  unsavedChanges,
  newStudent,
  reviewStudentEnrollment,
  printApplication,
}: StudentApplicationHeaderProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resource, setResource] = useState<StudentApplicationFilterResource>({ permissionToApprove: false });

  useEffect(() => {
    ApiService.studentApplications.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const currentStatusId = applicationDto?.applicationStatus?.id ?? applicationDto?.applicationStatusId;
  const requiresProgramStatus = applicationResource.displayFields?.some((x) => x.id === LkStudentDisplayFieldId.ProgramStatus);
  const hasExistingStudent = !!applicationDto?.studentId && applicationDto.studentId > 0;

  const refreshApplicationResponse = (reloadPage = false) => {
    return ApiService.studentApplications
      .getApplicationResponse(applicationDto?.applicationResponse?.id)
      .then((response) => {
        updateApplicationDto(response);
        if (reloadPage) {
          navigate("../");
          window.location.reload();
        }
        dispatch(setAppToast(newToast("success")));
      });
  };

  const saveApplicationStatus = (
    applicationStatusId: number,
    options?: {
      studentId?: number;
      reloadPage?: boolean;
      resetApproving?: boolean;
    }
  ) => {
    if (!applicationDto) return;

    if (options?.resetApproving ?? true) {
      updateIsApproving(false);
    }

    const payload: StudentApplicationResponseDto = {
      ...applicationDto,
      applicationStatusId,
      studentId: options?.studentId ?? applicationDto.studentId,
    };

    ApiService.studentApplications
      .updateApplicationStatus(payload)
      .then(() => refreshApplicationResponse(options?.reloadPage))
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const saveCurrentApplicationStatus = () => {
    if (!applicationDto?.applicationStatusId) return;

    saveApplicationStatus(applicationDto.applicationStatusId, {
      reloadPage: true,
      resetApproving: false,
    });
  };

  const buildReviewValidationErrors = (reviewerRequired = false) => {
    const customValidationErrors: ValidationErrors = {};

    if (requiresProgramStatus && !applicationDto?.applicationResponse?.programStatusId) {
      customValidationErrors.programStatusId = ["Program Status Required"];
    }

    if (reviewerRequired && !applicationDto?.applicationResponse?.reviewerId) {
      customValidationErrors.reviewerId = ["Reviewer Required"];
    }

    return customValidationErrors;
  };

  const validateReviewFields = (reviewerRequired = false) => {
    const customValidationErrors = buildReviewValidationErrors(reviewerRequired);

    if (Object.keys(customValidationErrors).length === 0) {
      return true;
    }

    setValidationErrors(customValidationErrors);
    dispatch(setAppToast(newToast("error", "Validation Error")));
    scrollToReviewCard();
    return false;
  };

  const handleStudentSaveError = (ex) => {
    dispatch(setAppToast(newToast("error")));
    setValidationErrors(ex.data);
  };

  const approveApplicationStart = () => {
    if (!applicationDto) return;

    updateIsApproving(true);
    const temp = deepCopy(applicationDto);
    temp.applicationStatus!.name = "Approving...";
    updateApplicationDto(temp);
  };

  const reviewApplicationStart = () => {
    if (!applicationDto) return;

    updateIsApproving(true);
    const temp = deepCopy(applicationDto);
    temp.applicationStatus!.name = "Entering Information...";
    temp.applicationStatusId = ApplicationStatus.StaffReview;
    updateApplicationDto(temp);
  };

  const approveApplication = (studentId: number) => {
    saveApplicationStatus(ApplicationStatus.Approved, {
      studentId,
      reloadPage: true,
      resetApproving: false,
    });
  };

  const updateReviewApplication = (studentId: number) => {
    if (!applicationDto?.applicationStatusId) return;

    saveApplicationStatus(applicationDto.applicationStatusId, {
      studentId,
      reloadPage: true,
      resetApproving: false,
    });
  };

  const save = () => {
    if (!validateReviewFields()) return;

    ApiService.students
      .saveCreate(newStudent)
      .then((studentId) => approveApplication(studentId))
      .catch(handleStudentSaveError);
  };

  const saveReview = () => {
    if (!validateReviewFields(true)) return;

    ApiService.students
      .saveCreate({ ...newStudent, isPartial: true })
      .then((studentId) => updateReviewApplication(studentId))
      .catch(handleStudentSaveError);
  };

  const updateReview = () => {
    setValidationErrors({});

    ApiService.students
      .saveUpdate({ ...newStudent, isPartial: true })
      .then((studentId) => updateReviewApplication(studentId))
      .catch(handleStudentSaveError);
  };

  const finishReview = () => {
    setValidationErrors({});

    ApiService.students
      .saveUpdate({ ...newStudent, isPartial: undefined })
      .then((studentId) => {
        ApiService.studentEnrollments.saveUpdate(reviewStudentEnrollment!).then(() => {
          approveApplication(studentId);
        });
      })
      .catch(handleStudentSaveError);
  };

  const menu = useMemo<MenuItemType[]>(() => {
    if (!applicationDto) return [];

    const menuItems: MenuItemType[] = [];

    resource.applicationStatuses?.forEach((status) => {
      if (status.id === ApplicationStatus.Approved && currentStatusId !== ApplicationStatus.Approved) {
        menuItems.push({
          label: status.name,
          action: () => approveApplicationStart(),
        });
      }

      if (
        status.id === ApplicationStatus.Waitlisted &&
        currentStatusId !== ApplicationStatus.Approved &&
        currentStatusId !== ApplicationStatus.Waitlisted
      ) {
        menuItems.push({
          label: status.name,
          action: () => saveApplicationStatus(ApplicationStatus.Waitlisted),
        });
      }

      if (
        status.id === ApplicationStatus.Pending &&
        currentStatusId !== 2 &&
        currentStatusId !== ApplicationStatus.Pending &&
        currentStatusId !== ApplicationStatus.StaffReview
      ) {
        menuItems.push({
          label: status.name,
          action: () => saveApplicationStatus(ApplicationStatus.Pending),
        });
      }

      if (
        status.id === ApplicationStatus.Declined &&
        currentStatusId !== ApplicationStatus.Approved &&
        currentStatusId !== ApplicationStatus.Declined
      ) {
        menuItems.push({
          label: <Typography color="error">{status.name}</Typography>,
          action: () => saveApplicationStatus(ApplicationStatus.Declined),
        });
      }

      if (status.id && status.id > 4 && status.id !== ApplicationStatus.StaffReview) {
        menuItems.push({
          label: status.name,
          action: () => saveApplicationStatus(status.id!),
        });
      }
    });

    return menuItems;
  }, [applicationDto, resource.applicationStatuses, currentStatusId]);

  const applicationStatusField = () => {
    let filteredOptions = resource.applicationStatuses;
    if (!resource.permissionToApprove) {
      filteredOptions = filteredOptions?.filter((o) => o.id !== ApplicationStatus.Approved);
    }
    if (applicationDto?.applicationStatusId === ApplicationStatus.StaffReview) {
      filteredOptions = filteredOptions?.filter((o) => o.id !== ApplicationStatus.Pending);
    }

    return (
      <Select
        label="Status"
        sx={{ width: "200px" }}
        value={resource.applicationStatuses?.find((x) => x.id === applicationDto?.applicationStatusId)}
        getOptionLabel={(option) => `${option.name}`}
        options={filteredOptions}
        onChange={(e, value) => {
          updateApplicationDto({ ...applicationDto, applicationStatusId: value.id });
        }}
      />
    );
  };

  if (!applicationDto) return null;

  const showApplicationStatus = !canReview || !isApproving;
  const showMenu = menu.length > 0 && !canReview;
  const showStatusField = menu.length > 0 && canReview && isApproving;
  const showContinueReview = isReviewing && !isApproving && canReview;
  const showReview = !isReviewing && !isApproving && canReview && applicationDto.applicationStatusId !== ApplicationStatus.Approved;
  const showFlagForReview = !isReviewing && isApproving && canReview && applicationDto.applicationStatusId === ApplicationStatus.StaffReview;
  const showInitialApprove =
    isApproving &&
    ((!isReviewing && !canReview) ||
      (canReview && applicationDto.applicationStatusId === ApplicationStatus.Approved && !hasExistingStudent));
  const showUpdateReview = isApproving && isReviewing && applicationDto.applicationStatusId === ApplicationStatus.StaffReview;
  const showFinishReview = isApproving && canReview && applicationDto.applicationStatusId === ApplicationStatus.Approved && hasExistingStudent;
  const showStatusSave =
    hasChanges &&
    [ApplicationStatus.Declined, ApplicationStatus.Waitlisted, ApplicationStatus.Pending].includes(applicationDto.applicationStatusId!);

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {applicationDto.lastName}, {applicationDto.firstName}
        </Typography>
        {showApplicationStatus && (
          <Typography fontWeight="light">Application Status: {applicationDto.applicationStatus?.name}</Typography>
        )}
        {showMenu && <DropDownMenu menuItems={menu} />}
        {showStatusField && <Stack>{applicationStatusField()}</Stack>}
        <UpdateButton label="Print" onClick={() => printApplication()} disabled={!unsavedChanges} />
      </Stack>

      <Stack direction="row" alignItems="center">
        <CloseButton />
        {showContinueReview && (
          <UpdateButton label="Continue Review" onClick={() => reviewApplicationStart()} disabled={!unsavedChanges} />
        )}
        {showReview && <UpdateButton label="Review" onClick={() => reviewApplicationStart()} disabled={!unsavedChanges} />}
        {showFlagForReview && <UpdateButton label="Flag for Review" onClick={() => saveReview()} disabled={!unsavedChanges} />}
        {showInitialApprove && (
          <SubmitButton label="Approve" onClick={() => save()} disabled={!unsavedChanges || !resource.permissionToApprove} />
        )}
        {showUpdateReview && (
          <UpdateButton
            label="Update Review"
            onClick={() => updateReview()}
            disabled={!unsavedChanges || !applicationDto.applicationStatusId}
          />
        )}
        {showFinishReview && (
          <SubmitButton
            label="Approve"
            onClick={() => finishReview()}
            disabled={!unsavedChanges || !resource.permissionToApprove}
          />
        )}
        {showStatusSave && <SubmitButton onClick={() => saveCurrentApplicationStatus()} disabled={!unsavedChanges} />}
      </Stack>
    </Stack>
  );
};
